/**
 * DashboardCharts.js
 * 3 charts pour le Dashboard admin :
 *  1. KycKybStatusChart       — Doubles donuts : statuts KYC utilisateurs + KYB organisations
 *  2. TransactionsTodayChart  — Barres groupées par heure (success / failed / pending)
 *  3. OrgLimitsChart          — Statistiques globales + barres volumes + jauges %
 *
 * Intégration dans Home.js :
 *   import DashboardCharts from "../components/chart/DashboardCharts";
 *   // Remplacer le <Row> contenant <Echart /> et <LineChart /> par :
 *   <DashboardCharts />
 *
 * Dépendance requise : npm install recharts
 */

import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  Col,
  Row,
  Spin,
  Typography,
  Tag,
  Space,
  Alert,
  Button,
} from "antd";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartTooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import {
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
  BankOutlined,
} from "@ant-design/icons";
import { useAdminPermissions } from "../../context/AdminContext";

const { Title, Text } = Typography;

// ─── Palette ─────────────────────────────────────────────────────────────────
const C = {
  approved: "#52c41a",
  pending: "#faad14",
  rejected: "#ff4d4f",
  not_submitted: "#bfbfbf",
  success: "#1890ff",
  failed: "#ff4d4f",
  processing: "#faad14",
  purple: "#722ed1",
};

// ─── Hook : fetch authentifié via AdminContext ────────────────────────────────
function useAuthFetch() {
  const { authToken } = useAdminPermissions();

  return useCallback(
    async (url) => {
      const res = await fetch(`${process.env.REACT_APP_API_URL || ""}${url}`, {
        headers: {
          "Content-Type": "application/json",
          ...(authToken && { Authorization: `Bearer ${authToken}` }),
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status} — ${url}`);
      return res.json();
    },
    [authToken],
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   1.  KYC + KYB — doubles donuts
   Endpoints :
     GET /api/submit/verification/all          → { data: [...], total: N }
     GET /api/admin/organisation-verification/all → { message, data: [...] }
   ═══════════════════════════════════════════════════════════════════════════ */
function KycKybStatusChart() {
  const authFetch = useAuthFetch();
  const [kycCounts, setKyc] = useState(null);
  const [kybCounts, setKyb] = useState(null);
  const [totals, setTotals] = useState({ kyc: 0, kyb: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [kycRes, kybRes] = await Promise.all([
        authFetch("/api/submit/verification/all"),
        authFetch("/api/admin/organisation-verification/all"),
      ]);

      // KYC → { data: [ { status, … } ], total }
      const kycItems = kycRes?.data || [];
      const kyc = kycItems.reduce(
        (acc, item) => {
          acc[item.status] = (acc[item.status] || 0) + 1;
          return acc;
        },
        { pending: 0, approved: 0, rejected: 0, not_submitted: 0 },
      );

      // KYB → { message, data: [ { status, … } ] }
      const kybItems = kybRes?.data || [];
      const kyb = kybItems.reduce(
        (acc, item) => {
          acc[item.status] = (acc[item.status] || 0) + 1;
          return acc;
        },
        { pending: 0, approved: 0, rejected: 0, not_submitted: 0 },
      );

      setKyc(kyc);
      setKyb(kyb);
      setTotals({ kyc: kycItems.length, kyb: kybItems.length });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    load();
  }, [load]);

  const toPie = (counts) =>
    [
      { name: "Approuvés", value: counts.approved, color: C.approved },
      { name: "En attente", value: counts.pending, color: C.pending },
      { name: "Rejetés", value: counts.rejected, color: C.rejected },
      {
        name: "Non soumis",
        value: counts.not_submitted,
        color: C.not_submitted,
      },
    ].filter((d) => d.value > 0);

  /* Label % à l'intérieur du donut */
  const PctLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
  }) => {
    if (percent < 0.06) return null;
    const RAD = Math.PI / 180;
    const r = innerRadius + (outerRadius - innerRadius) * 0.55;
    return (
      <text
        x={cx + r * Math.cos(-midAngle * RAD)}
        y={cy + r * Math.sin(-midAngle * RAD)}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={11}
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  /* Sous-panel réutilisable */
  const Panel = ({ title, icon, counts, total, color }) => (
    <Col xs={24} sm={12}>
      <div
        style={{
          background: `${color}0d`,
          borderRadius: 8,
          padding: "10px 14px",
          borderLeft: `4px solid ${color}`,
          marginBottom: 8,
        }}
      >
        <Space>
          {icon}
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {title}
            </Text>
            <div
              style={{
                fontSize: 22,
                fontWeight: "bold",
                color,
                lineHeight: 1.2,
              }}
            >
              {total}
            </div>
          </div>
          {counts?.pending > 0 && (
            <Tag color="warning" style={{ fontSize: 10 }}>
              {counts.pending} en attente
            </Tag>
          )}
        </Space>
      </div>

      {total === 0 ? (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Text type="secondary">Aucune donnée</Text>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={170}>
          <PieChart>
            <Pie
              data={toPie(counts)}
              cx="50%"
              cy="50%"
              innerRadius={44}
              outerRadius={72}
              dataKey="value"
              labelLine={false}
              label={PctLabel}
            >
              {toPie(counts).map((e, i) => (
                <Cell key={i} fill={e.color} />
              ))}
            </Pie>
            <RechartTooltip formatter={(v, n) => [`${v}`, n]} />
          </PieChart>
        </ResponsiveContainer>
      )}
    </Col>
  );

  return (
    <Card
      bordered={false}
      className="criclebox h-full"
      title={
        <Space>
          <SafetyCertificateOutlined style={{ color: "#1890ff" }} />
          <Title level={5} style={{ margin: 0 }}>
            Vérifications KYC & KYB
          </Title>
        </Space>
      }
      extra={
        <Button
          size="small"
          icon={<ReloadOutlined />}
          onClick={load}
          loading={loading}
        >
          Actualiser
        </Button>
      }
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert type="error" message="Erreur" description={error} showIcon />
      ) : (
        <>
          <Row gutter={[12, 12]}>
            <Panel
              title="KYC — Utilisateurs"
              icon={<UserOutlined style={{ color: "#1890ff", fontSize: 18 }} />}
              counts={kycCounts}
              total={totals.kyc}
              color="#1890ff"
            />
            <Panel
              title="KYB — Organisations"
              icon={<BankOutlined style={{ color: C.purple, fontSize: 18 }} />}
              counts={kybCounts}
              total={totals.kyb}
              color={C.purple}
            />
          </Row>

          {/* Légende commune */}
          <Row justify="center" gutter={[12, 4]} style={{ marginTop: 10 }}>
            {[
              { label: "Approuvés", color: C.approved },
              { label: "En attente", color: C.pending },
              { label: "Rejetés", color: C.rejected },
              { label: "Non soumis", color: C.not_submitted },
            ].map(({ label, color }) => (
              <Col key={label}>
                <Space size={4}>
                  <span
                    style={{
                      display: "inline-block",
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: color,
                    }}
                  />
                  <Text style={{ fontSize: 11 }}>{label}</Text>
                </Space>
              </Col>
            ))}
          </Row>
        </>
      )}
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   2.  Transactions du jour — barres groupées par heure
   Endpoint :
     GET /api/transactions  → tableau plat  [ { status, amount, createdAt, … } ]
     status ∈ "success" | "failed" | "pending"
   ═══════════════════════════════════════════════════════════════════════════ */
function TransactionsTodayChart() {
  const authFetch = useAuthFetch();
  const [bars, setBars] = useState([]);
  const [summary, setSummary] = useState({
    success: 0,
    failed: 0,
    pending: 0,
    amount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const raw = await authFetch("/api/transactions");
      // Tableau plat ou enveloppé
      const all = Array.isArray(raw) ? raw : raw?.data || [];
      const today = new Date().toISOString().split("T")[0];
      const txList = all.filter((tx) => (tx.createdAt || "").startsWith(today));

      let success = 0,
        failed = 0,
        pending = 0,
        amount = 0;
      const byHour = {};

      txList.forEach((tx) => {
        const s = (tx.status || "pending").toLowerCase();
        const h = new Date(tx.createdAt).getHours();
        const key = `${String(h).padStart(2, "0")}h`;

        if (!byHour[key])
          byHour[key] = { heure: key, Succès: 0, Échecs: 0, "En cours": 0 };

        if (s === "success") {
          success++;
          amount += tx.amount || 0;
          byHour[key]["Succès"]++;
        } else if (s === "failed") {
          failed++;
          byHour[key]["Échecs"]++;
        } else {
          pending++;
          byHour[key]["En cours"]++;
        }
      });

      setSummary({ success, failed, pending, amount });
      setBars(
        Object.values(byHour).sort((a, b) => a.heure.localeCompare(b.heure)),
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    load();
  }, [load]);

  const kpis = [
    {
      label: "Succès",
      value: summary.success,
      color: C.success,
      icon: <CheckCircleOutlined />,
    },
    {
      label: "Échecs",
      value: summary.failed,
      color: C.failed,
      icon: <CloseCircleOutlined />,
    },
    {
      label: "En cours",
      value: summary.pending,
      color: C.processing,
      icon: <ClockCircleOutlined />,
    },
  ];

  return (
    <Card
      bordered={false}
      className="criclebox h-full"
      title={
        <Space>
          <ClockCircleOutlined style={{ color: "#1890ff" }} />
          <Title level={5} style={{ margin: 0 }}>
            Transactions d'aujourd'hui
          </Title>
        </Space>
      }
      extra={
        <Button
          size="small"
          icon={<ReloadOutlined />}
          onClick={load}
          loading={loading}
        >
          Actualiser
        </Button>
      }
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert type="error" message="Erreur" description={error} showIcon />
      ) : (
        <>
          {/* KPI cards */}
          <Row gutter={[8, 8]} style={{ marginBottom: 14 }}>
            {kpis.map(({ label, value, color, icon }) => (
              <Col span={8} key={label}>
                <div
                  style={{
                    background: `${color}0d`,
                    borderRadius: 8,
                    padding: "10px 6px",
                    textAlign: "center",
                    borderTop: `3px solid ${color}`,
                  }}
                >
                  <div style={{ color, fontSize: 18, marginBottom: 2 }}>
                    {icon}
                  </div>
                  <div style={{ fontSize: 24, fontWeight: "bold", color }}>
                    {value}
                  </div>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {label}
                  </Text>
                </div>
              </Col>
            ))}
          </Row>

          {/* Montant succès */}
          <div
            style={{
              background: "#f6ffed",
              borderRadius: 8,
              padding: "8px 16px",
              marginBottom: 14,
              borderLeft: "4px solid #52c41a",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>
              Montant total (succès)
            </Text>
            <Text strong style={{ color: C.approved, fontSize: 15 }}>
              {summary.amount.toLocaleString("fr-FR")} XOF
            </Text>
          </div>

          {bars.length === 0 ? (
            <div style={{ textAlign: "center", padding: "28px 0" }}>
              <Text type="secondary">Aucune transaction aujourd'hui</Text>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={215}>
              <BarChart
                data={bars}
                margin={{ top: 5, right: 10, left: -20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="heure" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <RechartTooltip
                  contentStyle={{ borderRadius: 8, fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Succès" fill={C.success} radius={[3, 3, 0, 0]} />
                <Bar dataKey="Échecs" fill={C.failed} radius={[3, 3, 0, 0]} />
                <Bar
                  dataKey="En cours"
                  fill={C.processing}
                  radius={[3, 3, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}

          {summary.failed > 0 && (
            <Alert
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              message={`${summary.failed} transaction(s) en échec aujourd'hui`}
              style={{ marginTop: 12, fontSize: 12 }}
            />
          )}
        </>
      )}
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   3.  Limites organisations — stats globales
   Endpoint :
     GET /api/admin/organisations/limits/stats
     → { message, data: {
           total_organisations, active_limits, inactive_limits,
           over_daily_limit, over_monthly_limit,
           total_daily_usage, total_monthly_usage,
           average_daily_percentage, average_monthly_percentage
        } }
   ═══════════════════════════════════════════════════════════════════════════ */
function OrgLimitsChart() {
  const authFetch = useAuthFetch();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await authFetch("/api/admin/organisations/limits/stats");
      setStats(res?.data || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    load();
  }, [load]);

  /* Données bar chart volume */
  const volumeData = (s) => [
    {
      name: "Quotidien",
      Utilisé: s.total_daily_usage || 0,
      Dépassé: s.over_daily_limit || 0,
    },
    {
      name: "Mensuel",
      Utilisé: s.total_monthly_usage || 0,
      Dépassé: s.over_monthly_limit || 0,
    },
  ];

  /* Données barres % moyens */
  const pctData = (s) => [
    {
      name: "% moy. quotidien",
      value: parseFloat((s.average_daily_percentage * 100).toFixed(6)),
      color: C.success,
    },
    {
      name: "% moy. mensuel",
      value: parseFloat((s.average_monthly_percentage * 100).toFixed(6)),
      color: C.purple,
    },
  ];

  /* Tooltip personnalisé */
  const PctTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div
        style={{
          background: "#fff",
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          padding: "8px 12px",
          fontSize: 12,
        }}
      >
        <Text strong>{d.name}</Text>
        <div style={{ color: d.color, fontWeight: "bold" }}>{d.value}%</div>
      </div>
    );
  };

  /* KPI cards de la section */
  const kpiList = stats
    ? [
        {
          label: "Organisations",
          value: stats.total_organisations,
          color: "#1890ff",
        },
        {
          label: "Limites actives",
          value: stats.active_limits,
          color: C.approved,
        },
        {
          label: "Dépassements/jour",
          value: stats.over_daily_limit,
          color: stats.over_daily_limit > 0 ? C.failed : C.approved,
        },
        {
          label: "Dépassements/mois",
          value: stats.over_monthly_limit,
          color: stats.over_monthly_limit > 0 ? C.failed : C.approved,
        },
      ]
    : [];

  return (
    <Card
      bordered={false}
      className="criclebox"
      title={
        <Space>
          <WarningOutlined style={{ color: "#faad14" }} />
          <Title level={5} style={{ margin: 0 }}>
            Limites de transactions — Vue globale
          </Title>
        </Space>
      }
      extra={
        <Button
          size="small"
          icon={<ReloadOutlined />}
          onClick={load}
          loading={loading}
        >
          Actualiser
        </Button>
      }
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert type="error" message="Erreur" description={error} showIcon />
      ) : !stats ? (
        <Alert type="info" message="Aucune statistique disponible" showIcon />
      ) : (
        <>
          {/* ── KPI row ── */}
          <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
            {kpiList.map(({ label, value, color }) => (
              <Col xs={12} sm={6} key={label}>
                <div
                  style={{
                    background: `${color}0d`,
                    borderRadius: 10,
                    padding: "12px 14px",
                    textAlign: "center",
                    borderTop: `3px solid ${color}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: 26,
                      fontWeight: "bold",
                      color,
                      lineHeight: 1.2,
                    }}
                  >
                    {(value ?? 0).toLocaleString("fr-FR")}
                  </div>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {label}
                  </Text>
                </div>
              </Col>
            ))}
          </Row>

          <Row gutter={[24, 0]}>
            {/* ── Volume quotidien vs mensuel ── */}
            <Col xs={24} md={14}>
              <Text
                strong
                style={{ display: "block", marginBottom: 8, fontSize: 13 }}
              >
                Volume d'utilisation (nb transactions)
              </Text>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart
                  data={volumeData(stats)}
                  margin={{ top: 5, right: 20, left: -20, bottom: 5 }}
                  barSize={36}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <RechartTooltip
                    contentStyle={{ borderRadius: 8, fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Utilisé" fill={C.success} radius={[4, 4, 0, 0]}>
                    <LabelList
                      dataKey="Utilisé"
                      position="top"
                      style={{ fontSize: 11 }}
                    />
                  </Bar>
                  <Bar dataKey="Dépassé" fill={C.failed} radius={[4, 4, 0, 0]}>
                    <LabelList
                      dataKey="Dépassé"
                      position="top"
                      style={{ fontSize: 11 }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Col>

            {/* ── Taux d'utilisation moyen (barres horizontales) ── */}
            <Col xs={24} md={10}>
              <Text
                strong
                style={{ display: "block", marginBottom: 8, fontSize: 13 }}
              >
                Taux d'utilisation moyen
              </Text>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart
                  layout="vertical"
                  data={pctData(stats)}
                  margin={{ top: 10, right: 55, left: 10, bottom: 5 }}
                  barSize={24}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    horizontal={false}
                    stroke="#f0f0f0"
                  />
                  <XAxis
                    type="number"
                    tickFormatter={(v) => `${v}%`}
                    tick={{ fontSize: 11 }}
                    domain={[0, "auto"]}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 11 }}
                    width={130}
                  />
                  <RechartTooltip content={<PctTooltip />} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {pctData(stats).map((e, i) => (
                      <Cell key={i} fill={e.color} />
                    ))}
                    <LabelList
                      dataKey="value"
                      position="right"
                      formatter={(v) => `${v}%`}
                      style={{ fontSize: 11, fill: "#555" }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Col>
          </Row>

          {/* Alerte dépassements */}
          {(stats.over_daily_limit > 0 || stats.over_monthly_limit > 0) && (
            <Alert
              type="error"
              showIcon
              icon={<WarningOutlined />}
              message={`${stats.over_daily_limit} dépassement(s) quotidien(s) — ${stats.over_monthly_limit} mensuel(s)`}
              style={{ marginTop: 16, fontSize: 12 }}
            />
          )}
        </>
      )}
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Export principal
   ═══════════════════════════════════════════════════════════════════════════ */
export default function DashboardCharts() {
  return (
    <>
      {/* Ligne 1 : KYC/KYB  +  Transactions du jour */}
      <Row gutter={[24, 0]}>
        <Col xs={24} sm={24} md={12} lg={12} xl={10} className="mb-24">
          <KycKybStatusChart />
        </Col>
        <Col xs={24} sm={24} md={12} lg={12} xl={14} className="mb-24">
          <TransactionsTodayChart />
        </Col>
      </Row>

      {/* Ligne 2 : Limites (pleine largeur) */}
      <Row gutter={[24, 0]}>
        <Col xs={24} className="mb-24">
          <OrgLimitsChart />
        </Col>
      </Row>
    </>
  );
}
